import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { DATE_FORMAT } from '../../constants';
import { useGetTournament } from '../../hooks/useGetTournament';
import { Loader } from '../Shared/Loader/Loader';
import { ChallengeStatusBadge } from '../Shared/ChallengeStatusBadge/ChallengeStatusBadge';
import { TwitchLink } from '../Shared/TwitchLink/TwitchLink';
import styles from './ChallengeDetail.module.scss';

export const ChallengeDetail = () => {
    const { id, challengeId } = useParams<{ id: string; challengeId: string }>();
    const navigate = useNavigate();
    const { data: tournament, isLoading } = useGetTournament(Number(id));

    const decodedChallengeId = decodeURIComponent(challengeId ?? '');
    const challenge = tournament?.challenges?.find((item) => item.challengeId === decodedChallengeId);

    // Twitch is taken only from the first captain across all participants.
    const host = challenge?.teams.flatMap((team) => team.participants).find((p) => p.isCaptain);

    if (isLoading) {
        return <Loader />;
    }

    if (!tournament || !challenge) {
        return <div className={styles.empty}>Challenge not found</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.backButton} onClick={() => navigate(`/tournaments/${id}`)}>← Back</div>
                <div className={styles.title}>Challenge Details</div>
                <div className={styles.editButton} onClick={() => navigate(`/tournaments/${id}/challenges/${encodeURIComponent(challenge.challengeId)}/edit`)}>
                    Edit challenge
                </div>
            </div>

            <div className={styles.infoCard}>
                <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Status</div>
                        <div className={styles.value}>
                            <ChallengeStatusBadge status={challenge.status} />
                        </div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Challenge ID</div>
                        <div className={styles.value}>{challenge.challengeId}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Tournament ID</div>
                        <div className={styles.value}>{challenge.tournamentId}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Next Challenge ID</div>
                        <div className={styles.value}>{challenge.nextChallengeId || '—'}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Stage</div>
                        <div className={styles.value}>{challenge.stage}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Round</div>
                        <div className={styles.value}>{challenge.roundName}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Scheduled At</div>
                        <div className={styles.value}>{format(new Date(challenge.scheduledAt), DATE_FORMAT)}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Capacity</div>
                        <div className={styles.value}>{challenge.capacity}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Auto Win</div>
                        <div className={styles.value}>{challenge.isAutoWin ? 'Yes' : 'No'}</div>
                    </div>
                    <div className={styles.infoItem}>
                        <div className={styles.label}>Abandoned</div>
                        <div className={styles.value}>{challenge.isAbandoned ? 'Yes' : 'No'}</div>
                    </div>
                </div>
            </div>

            <div className={styles.sectionTitle}>Teams ({challenge.teams.length})</div>
            <div className={styles.teams}>
                {challenge.teams.map((team) => (
                    <div key={team.name} className={styles.teamCard}>
                        <div className={styles.teamName}>{team.name}</div>
                        <div className={styles.teamMeta}>Capacity: {team.capacity} | Full: {team.isFull ? 'Yes' : 'No'} | Created: {format(new Date(team.createdAt), DATE_FORMAT)}</div>
                        <div className={styles.participants}>
                            {team.participants.length === 0 ? (
                                <div className={styles.result}>No participants</div>
                            ) : (
                                team.participants.map((participant) => (
                                    <div key={participant.userId} className={styles.participant}>
                                        <span>
                                            {participant.gameNickName || participant.userId}
                                            {host && participant.userId === host.userId && (
                                                <TwitchLink handle={host.linkedAccounts?.Twitch} />
                                            )}
                                            {participant.gameRating !== null ? ` (Rating: ${participant.gameRating})` : ''}
                                        </span>
                                        <span className={styles.result}>
                                            {participant.result?.gameResult || 'No result'}
                                            {participant.result && participant.result.score !== null ? ` / Score: ${participant.result.score}` : ''}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
